import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { orderService, userService } from "../../../Core/Api/services";
import TableCellName from "../../Table/TableCellName";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { UserAdvancedInfos } from "../../../Core/Api/Types/user-advanced-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalAddCustomerElementProps = {
  onClose?: (props: CloseClbProps) => void;
  context: "Create" | "Edit";
  customer?: DbCustomerInfos;
};
export type ModalAddCustomerProps = CommonModalProps<ModalAddCustomerElementProps>;

const ModalAddCustomer = React.forwardRef<any, ModalAddCustomerProps>((props, ref) => {
  const { closeModal, onClose, context, customer, ...modalProps } = props;
  const customSnackbar = useCustomSnackbar();
  const { t } = useTranslation();

  // about users
  const [usersPropositions, setUsersPropositions] = React.useState<UserAdvancedInfos[]>([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>([]);
  const [costumerLinkedUsers, setCostumerLinkedUsers] = React.useState<UserAdvancedInfos[]>([]);
  const [searchUserPattern, setSearchUserPattern] = React.useState<string>("");
  const [initialUserIds, setInitialUserIds] = React.useState<number[]>([]);
  const [initialUsers, setInitialUsers] = React.useState<UserAdvancedInfos[]>([]);
  React.useEffect(() => {
    if (searchUserPattern.length > 0) {
      userService.SearchUsers(searchUserPattern).then((response) => {
        if (response.users) {
          setUsersPropositions(response.users);
        }
      });
    }
  }, [searchUserPattern]);

  // about customer
  const [customerName, setCustomerName] = React.useState<string>("");
  const [customerExternalId, setCustomerExternalId] = React.useState<string>("");
  const [streetNumber, setStreetNumber] = React.useState<string>("");
  const [streetName, setStreetrName] = React.useState<string>("");
  const [postalCode, setPostalCode] = React.useState<string>("");
  const [city, setCity] = React.useState<string>("");
  const [country, setCountry] = React.useState<string>("");

  // Edit case
  React.useEffect(() => {
    if (context === "Edit") {
      if (customer?.name) setCustomerName(customer?.name);
      if (customer?.customerCodeId) setCustomerExternalId(customer?.customerCodeId);
      if (customer?.address) setStreetNumber(customer?.address.split(" ")[0]);
      if (customer?.address) setStreetrName(customer?.address.substring(customer?.address.indexOf(" ") + 1));
      if (customer?.zipCode) setPostalCode(customer?.zipCode);
      if (customer?.city) setCity(customer?.city);
      if (customer?.country) setCountry(customer?.country);
      userService.GetUsersListByCustomerId(customer!.id).then((response) => {
        if (response.users) {
          setCostumerLinkedUsers(response.users);
          setInitialUsers(response.users);
          setInitialUserIds(response.users.map((user) => user.userInfos?.id!));
          setSelectedUserIds(response.users.map((user) => user.userInfos?.id!));
        }
      });
    }
  }, []);

  //Actions
  const handleConfirmClick = () => {
    if (context === "Create") {
      let newCustomer: DbCustomerInfos = new DbCustomerInfos({
        disabled: true,
        addedManually: true,
        name: customerName,
        sapOrExternalId: customerExternalId,
        city: city,
        country: country,
        address: streetNumber + " " + streetName,
        zipCode: postalCode,
        isNew: true,
      });
      orderService.CreateCustomer(newCustomer).then((response) => {
        if (response.objectId) {
          customSnackbar(t("alert:success.customerCreated"), "success");
          //add custermerId to users selected
          newCustomer.id = response.objectId;
          let customers = [newCustomer];
          // newCustomer is now ready
          costumerLinkedUsers?.forEach((user) => {
            // here check if user already have linked customers
            if (user.userInfos?.customerIds) {
              user.userInfos?.customerIds.forEach((id) =>
                customers.push(new DbCustomerInfos({ id: id, addedManually: false, disabled: false }))
              );
            }
            userService.UpdateUser({ userInfos: user.userInfos, userCustomersLinks: customers }).then((response) => {
              customSnackbar(t("alert:success.userUpdated", { id: user.userInfos?.id }), "success");
            });
          });
        }
      });
    } else {
      let EditedCustomer: DbCustomerInfos = new DbCustomerInfos({
        name: customerName,
        sapOrExternalId: customerExternalId,
        city: city,
        country: country,
        address: streetNumber + " " + streetName,
        zipCode: customer!.zipCode,
      });
      orderService.EditCustomer(EditedCustomer).then((response) => {
        if (response.success) {
          let usersAddedIds = selectedUserIds.filter((id) => !initialUserIds.includes(id));
          let usersDeletedIds = initialUserIds.filter((id) => !selectedUserIds.includes(id));
          let userAdded = costumerLinkedUsers.filter((user) => usersAddedIds.includes(user.userInfos?.id!));
          let usersDeleted = initialUsers.filter((user) => usersDeletedIds.includes(user.userInfos?.id!));

          // update new linked users
          if (userAdded.length > 0)
            userAdded.forEach((user) => {
              let customers = [customer] as DbCustomerInfos[];
              //here check if user already have linked customers
              if (user.userInfos?.customerIds) {
                user.userInfos?.customerIds.forEach((id) =>
                  customers!.push(new DbCustomerInfos({ id: id, addedManually: false, disabled: false }))
                );
              }
              userService.UpdateUser({ userInfos: user.userInfos, userCustomersLinks: customers }).then((response) => {
                customSnackbar(t("alert:success.userUpdated", { id: user.userInfos?.id }), "success");
              });
            });

          // update users which no longer linked
          if (usersDeleted.length > 0)
            usersDeleted?.forEach((user) => {
              let newCustomerIds = user.userInfos?.customerIds?.filter((id) => id !== customer!.id);
              if (newCustomerIds) {
                let newCustomers: DbCustomerInfos[] = [];
                newCustomerIds.forEach((id) => {
                  newCustomers.push(
                    new DbCustomerInfos({
                      id: id,
                      addedManually: customer?.addedManually,
                      disabled: customer?.disabled,
                    })
                  );
                });
                userService
                  .UpdateUser({ userInfos: user.userInfos, userCustomersLinks: newCustomers })
                  .then((response) => {
                    customSnackbar(t("alert:success.userUpdated", { id: user.userInfos?.id }), "success");
                  });
              }
            });
        }
      });
    }
    onClose && onClose({ confirm: true });
    closeModal();
  };

  const handleAddUser = (userToAdd) => {
    if (!selectedUserIds.includes(userToAdd.userInfos?.id)) {
      setCostumerLinkedUsers((current) => [...current, userToAdd]);
      setSelectedUserIds((current) => [...current, userToAdd.userInfos?.id]);
    }
  };

  const handleRemoveUser = (userToRemove) => {
    setCostumerLinkedUsers((current) => current.filter((user) => user.userInfos?.id !== userToRemove.userInfos?.id));
    setSelectedUserIds((current) => current.filter((id) => id !== userToRemove.userInfos?.id));
  };

  const redirectToUserCreation = () => {
    //redirect
  };

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  return (
    <Modal
      {...modalProps}
      title={context === "Create" ? t("common:createCustomer") : t("common:editCustomer")}
      reference={ref}
      closeModal={handleCancelClick}
      width={680}
      footer={
        <Stack direction="row" justifyContent="space-between">
          {/* <Button onClick={handleCancelClick}>Cancel</Button> */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={
              customerName.length === 0 ||
              customerExternalId.length === 0 ||
              streetNumber.length === 0 ||
              streetName.length === 0 ||
              postalCode.length === 0
            }
          >
            {context === "Create" ? t("common:createCustomer") : t("common:editCustomer")}
          </Button>
        </Stack>
      }
    >
      <FormControl sx={{ width: "100%", mb: 2 }}>
        <InputLabel shrink htmlFor="customer-name">
          {t("field:label.customerName")}
        </InputLabel>
        <OutlinedInput
          id="customer-name"
          value={customerName}
          onChange={(e) => setCustomerName(e.currentTarget.value)}
        />
      </FormControl>
      <FormControl sx={{ width: "100%" }}>
        <InputLabel shrink htmlFor="external-id">
          {t("field:label.externalIdLowerCase")}
        </InputLabel>

        <OutlinedInput
          id="external-id"
          value={customerExternalId}
          onChange={(e) => setCustomerExternalId(e.currentTarget.value)}
        />
        <FormHelperText>
          <Typography color="primary.text" variant="body2" component="span">
            {t("field:helper.customerExternalId")}
          </Typography>
        </FormHelperText>
      </FormControl>
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5, mt: 1 }}>
          {t("common:address")}
        </Typography>
        <Typography color="text.tier" mb={2}>
          {t("modal:body.addressTier")}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item md={4}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="street-number">
                {t("common:streetNumber")}
              </InputLabel>
              <OutlinedInput
                id="street-number"
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.currentTarget.value)}
              />
            </FormControl>
          </Grid>
          <Grid item md={8}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="street-name">
                {t("common:streetName")}
              </InputLabel>
              <OutlinedInput
                id="street-name"
                value={streetName}
                onChange={(e) => setStreetrName(e.currentTarget.value)}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item md={4}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="postal-code">
                {t("common:postalCode")}
              </InputLabel>
              <OutlinedInput
                id="postal-code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.currentTarget.value)}
              />
            </FormControl>
          </Grid>
          <Grid item md={4}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="city">
                {t("common:city")}
              </InputLabel>
              <OutlinedInput id="city" value={city} onChange={(e) => setCity(e.currentTarget.value)} />
            </FormControl>
          </Grid>
          <Grid item md={4}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="country">
                {t("common:country")}
              </InputLabel>
              <OutlinedInput id="country" value={country} onChange={(e) => setCountry(e.currentTarget.value)} />
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
          {t("modal:body.userManagement")}
        </Typography>
        <Typography color="text.tier" mb={3}>
          {t("modal:body.userManagementTier")}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <OutlinedInput
            label={t("common:search")}
            sx={{ flex: 1 }}
            placeholder={t("common:search")}
            onChange={(e) => setSearchUserPattern(e.currentTarget.value)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
          <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={() => redirectToUserCreation()}>
            {t("button:createUser")}
          </Button>
        </Stack>
        {searchUserPattern.length > 0 && (
          <Box
            sx={{
              border: "1px solid",
              p: "12px 13px",
              mt: 2,
              maxHeight: "140px",
              overflow: "scroll",
              borderColor: "backgroundAlpha.main",
            }}
          >
            <Stack spacing={1}>
              {usersPropositions?.map((user) => (
                <Stack
                  key={user.userInfos?.id}
                  spacing={3}
                  direction="row"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  mb={1}
                >
                  <TableCellName
                    key={user.userInfos?.id}
                    data={{
                      value: {
                        firstname: user.userInfos?.firstName || "-",
                        lastname: user.userInfos?.lastName || "-",
                        id: user.userInfos?.id!,
                      },
                    }}
                  />
                  <IconButton onClick={() => handleAddUser(user)}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              {usersPropositions.length === 0 && <Typography>{t("modal:body.noResultsFound")}</Typography>}
            </Stack>
          </Box>
        )}
        <Divider sx={{ mt: 3, mb: 3 }} />
        {costumerLinkedUsers.length > 0 && (
          <>
            <Typography color="text.tier" variant="h6" mt={3} mb={2}>
              {t("modal:body.usersAdded", { count: costumerLinkedUsers.length })}
            </Typography>
            <Stack spacing={1}>
              {costumerLinkedUsers.map((user, index) => (
                <Stack direction="row" alignItems="center" justifyContent="space-between" key={index}>
                  <TableCellName
                    key={user.userInfos?.id}
                    data={{
                      value: {
                        firstname: user.userInfos?.firstName || "-",
                        lastname: user.userInfos?.lastName || "-",
                        id: user.userInfos?.id!,
                      },
                    }}
                  />
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton color="error" onClick={() => handleRemoveUser(user)}>
                      <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          </>
        )}
        {costumerLinkedUsers.length === 0 && (
          <Typography color="text.tier" variant="h6" mt={3} mb={2}>
            {t("modal:body.usersAdded", { count: 0 })}
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={3}></Stack>
    </Modal>
  );
});

export default ModalAddCustomer;
